import React, { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  CreditCardIcon,
  MagnifyingGlassIcon,
  EyeIcon,
  PlusIcon,
  ShieldCheckIcon,
  UserCircleIcon,
} from '@heroicons/react/24/outline';
import Card from '../components/UI/Card';
import Button from '../components/UI/Button';
import StatsCard from '../components/UI/StatsCard';
import CreditDetailModal from '../components/credits/CreditDetailModal';
import AddCreditModal from '../components/credits/AddCreditModal';
import creditService, { CreditListItem } from '../api/services/credit.service';

/* =========================================================
   Source badges — one label/color per source
========================================================= */

const SOURCE_META: Record<
  string,
  { label: string; className: string }
> = {
  'verify.et': {
    label: 'Verify.et',
    className: 'bg-secondary-100 text-secondary-700',
  },
  system: {
    label: 'Admin',
    className: 'bg-primary-100 text-primary-700',
  },
  referral: {
    label: 'Referral',
    className: 'bg-purple-100 text-purple-700',
  },
  signup: {
    label: 'Signup',
    className: 'bg-emerald-100 text-emerald-700',
  },
  game: {
    label: 'Game',
    className: 'bg-amber-100 text-amber-700',
  },
  transfer: {
    label: 'Transfer',
    className: 'bg-sky-100 text-sky-700',
  },
};

function sourceMeta(source: string) {
  return (
    SOURCE_META[source] || {
      label: source,
      className: 'bg-gray-100 text-gray-700',
    }
  );
}

const statusBadge = (status: string) => {
  if (status === 'completed') return 'bg-green-50 text-green-700';
  if (status === 'pending') return 'bg-yellow-50 text-yellow-700';
  return 'bg-red-50 text-red-700';
};

const Credit: React.FC = () => {
  const queryClient = useQueryClient();

  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [localSearch, setLocalSearch] = useState('');
  const [sourceFilter, setSourceFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<
    'all' | 'pending' | 'completed' | 'failed'
  >('all');
  const [receiverFilter, setReceiverFilter] = useState<string>('');

  /* Lazy receiver names list — fetched only when the dropdown is touched */
  const [receiverNames, setReceiverNames] = useState<string[]>([]);
  const [receiverNamesLoading, setReceiverNamesLoading] = useState(false);
  const [receiverNamesFetched, setReceiverNamesFetched] = useState(false);

  const [detailId, setDetailId] = useState<string | null>(null);
  const [isAddOpen, setIsAddOpen] = useState(false);

  const ensureReceiverNames = async () => {
    if (receiverNamesFetched || receiverNamesLoading) return;
    setReceiverNamesLoading(true);
    try {
      const names = await creditService.getReceiverNames();
      setReceiverNames(names);
      setReceiverNamesFetched(true);
    } catch (err) {
      console.warn('Failed to fetch receiver names:', err);
    } finally {
      setReceiverNamesLoading(false);
    }
  };

  /* STATS */
  const { data: stats } = useQuery({
    queryKey: ['credit-stats'],
    queryFn: () => creditService.getStats(),
    staleTime: 5 * 60 * 1000,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });

  /* LIST */
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: [
      'credits',
      page,
      limit,
      searchTerm,
      sourceFilter,
      statusFilter,
      receiverFilter,
    ],
    queryFn: () =>
      creditService.getAll({
        page,
        limit,
        search: searchTerm || undefined,
        source: sourceFilter as any,
        status: statusFilter,
        receiverName: receiverFilter || undefined,
      }),
    staleTime: 5 * 60 * 1000,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });

  const credits = data?.credits || [];
  const pagination = data?.pagination || {
    page: 1,
    limit: 10,
    total: 0,
    pages: 0,
  };

  const handleSearchSubmit = () => {
    setSearchTerm(localSearch);
    setPage(1);
  };

  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') handleSearchSubmit();
  };

  const handleClearFilters = () => {
    setLocalSearch('');
    setSearchTerm('');
    setSourceFilter('all');
    setStatusFilter('all');
    setReceiverFilter('');
    setPage(1);
  };

  const handleCreditAdded = () => {
    queryClient.invalidateQueries({ queryKey: ['credits'] });
    queryClient.invalidateQueries({ queryKey: ['credit-stats'] });
    queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    queryClient.invalidateQueries({ queryKey: ['users'] });
  };

  const s = stats || {
    totalCredits: 0,
    todayCredits: 0,
    verifyCredits: 0,
    todayVerifyCredits: 0,
    adminCredits: 0,
    todayAdminCredits: 0,
    totalTransactions: 0,
  };

  const statCards = [
    {
      title: 'Total Credits',
      value: `ETB ${s.totalCredits.toLocaleString()}`,
      todayValue: `ETB ${s.todayCredits.toLocaleString()}`,
      icon: <CreditCardIcon className="w-5 h-5" />,
      color: 'primary' as const,
      showToday: true,
    },
    {
      title: 'Verify.et Credits',
      value: `ETB ${s.verifyCredits.toLocaleString()}`,
      todayValue: `ETB ${s.todayVerifyCredits.toLocaleString()}`,
      icon: <ShieldCheckIcon className="w-5 h-5" />,
      color: 'secondary' as const,
      showToday: true,
    },
    {
      title: 'Admin Credits',
      value: `ETB ${s.adminCredits.toLocaleString()}`,
      todayValue: `ETB ${s.todayAdminCredits.toLocaleString()}`,
      icon: <UserCircleIcon className="w-5 h-5" />,
      color: 'warning' as const,
      showToday: true,
    },
    {
      title: 'Total Transactions',
      value: s.totalTransactions.toLocaleString(),
      todayValue: undefined,
      icon: <CreditCardIcon className="w-5 h-5" />,
      color: 'success' as const,
      showToday: false,
    },
  ];

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Credit</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            Manage credit transactions and balances.
          </p>
        </div>
        <Button size="sm" onClick={() => setIsAddOpen(true)}>
          <PlusIcon className="w-4 h-4 mr-1.5" />
          Add Credit
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {statCards.map((stat, i) => (
          <StatsCard key={i} {...stat} />
        ))}
      </div>

      {/* Filters */}
      <Card padding="sm" className="mb-4">
        <div className="flex flex-col md:flex-row md:items-center gap-2">
          <div className="relative flex-1">
            <MagnifyingGlassIcon className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              placeholder="Search by telegram id, reference, sender..."
              value={localSearch}
              onChange={(e) => setLocalSearch(e.target.value)}
              onKeyDown={handleSearchKeyDown}
              className="w-full text-sm border border-gray-200 rounded-lg pl-9 pr-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-400"
            />
          </div>

          <select
            value={sourceFilter}
            onChange={(e) => {
              setSourceFilter(e.target.value);
              setPage(1);
            }}
            className="text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-400"
          >
            <option value="all">All Sources</option>
            <option value="verify.et">Verify.et</option>
            <option value="system">Admin / System</option>
            <option value="referral">Referral</option>
            <option value="signup">Signup</option>
            <option value="game">Game</option>
            <option value="transfer">Transfer</option>
          </select>

          <select
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value as any);
              setPage(1);
            }}
            className="text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-400"
          >
            <option value="all">All Status</option>
            <option value="completed">Completed</option>
            <option value="pending">Pending</option>
            <option value="failed">Failed</option>
          </select>

          {/* Receiver filter — lazy loads distinct names on first touch */}
          <select
            value={receiverFilter}
            onMouseDown={() => void ensureReceiverNames()}
            onFocus={() => void ensureReceiverNames()}
            onChange={(e) => {
              setReceiverFilter(e.target.value);
              setPage(1);
            }}
            className="text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-400"
          >
            <option value="">
              {receiverNamesLoading ? 'Loading receivers…' : 'All Receivers'}
            </option>
            {receiverNames.map((name) => (
              <option key={name} value={name}>
                {name}
              </option>
            ))}
          </select>

          <button
            onClick={handleSearchSubmit}
            className="text-sm px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600"
          >
            Search
          </button>

          {(searchTerm ||
            sourceFilter !== 'all' ||
            statusFilter !== 'all' ||
            receiverFilter) && (
            <button
              onClick={handleClearFilters}
              className="text-sm px-4 py-2 text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50"
            >
              Clear
            </button>
          )}
        </div>
      </Card>

      {/* Table */}
      <Card padding="sm">
        {isLoading ? (
          <div className="flex items-center justify-center py-10">
            <div className="animate-spin rounded-full h-8 w-8 border-4 border-solid border-primary-500 border-t-transparent" />
          </div>
        ) : error ? (
          <div className="text-center py-6">
            <p className="text-sm text-red-500">
              {error instanceof Error ? error.message : 'Failed to load credits'}
            </p>
            <button
              onClick={() => refetch()}
              className="mt-3 px-4 py-2 bg-primary-500 text-white rounded-lg text-sm hover:bg-primary-600"
            >
              Retry
            </button>
          </div>
        ) : credits.length === 0 ? (
          <div className="text-center py-10">
            <p className="text-sm text-gray-500">No credit transactions found</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="text-left text-[10px] text-gray-500 uppercase tracking-wide border-b border-gray-100">
                  <th className="px-3 py-2 font-medium">User</th>
                  <th className="px-3 py-2 font-medium">Type</th>
                  <th className="px-3 py-2 font-medium">Source</th>
                  <th className="px-3 py-2 font-medium">Amount</th>
                  <th className="px-3 py-2 font-medium">Receiver</th>
                  <th className="px-3 py-2 font-medium">Ref</th>
                  <th className="px-3 py-2 font-medium">Status</th>
                  <th className="px-3 py-2 font-medium">Date</th>
                  <th className="px-3 py-2 font-medium text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {credits.map((c: CreditListItem) => {
                  const sm = sourceMeta(c.source);
                  return (
                    <tr
                      key={c.id}
                      className="border-b border-gray-50 hover:bg-gray-50/60"
                    >
                      <td className="px-3 py-3">
                        <p className="text-sm font-medium text-gray-900">
                          {c.userName}
                        </p>
                        <p className="text-[10px] text-gray-500">
                          {c.telegramId}
                        </p>
                      </td>
                      <td className="px-3 py-3 text-xs text-gray-700 capitalize">
                        {c.type.replace('_', ' ')}
                      </td>
                      <td className="px-3 py-3">
                        <span
                          className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-medium ${sm.className}`}
                        >
                          {sm.label}
                        </span>
                      </td>
                      <td className="px-3 py-3 text-sm font-semibold text-gray-900">
                        {c.currency} {c.amount.toLocaleString()}
                      </td>
                      <td className="px-3 py-3 text-xs text-gray-700 truncate max-w-[140px]">
                        {c.receiverName || '—'}
                      </td>
                      <td className="px-3 py-3 text-xs text-gray-600 truncate max-w-[120px]">
                        {c.referenceNumber || '—'}
                      </td>
                      <td className="px-3 py-3">
                        <span
                          className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-medium uppercase ${statusBadge(
                            c.status
                          )}`}
                        >
                          {c.status}
                        </span>
                      </td>
                      <td className="px-3 py-3 text-xs text-gray-600 whitespace-nowrap">
                        {new Date(c.createdAt).toLocaleDateString()}
                      </td>
                      <td className="px-3 py-3 text-right">
                        <button
                          onClick={() => setDetailId(c.id)}
                          className="p-1.5 rounded-lg hover:bg-primary-50 text-primary-600 transition-colors"
                          title="View"
                        >
                          <EyeIcon className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {pagination.total > 0 && (
          <div className="flex items-center justify-between px-3 py-3 border-t border-gray-100">
            <p className="text-xs text-gray-500">
              Page {pagination.page} of {pagination.pages} ·{' '}
              {pagination.total} total
            </p>
            <div className="flex items-center gap-1.5">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1}
                className="px-2.5 py-1 text-xs rounded-md border border-gray-200 disabled:opacity-40 hover:bg-gray-50"
              >
                Prev
              </button>
              <button
                onClick={() =>
                  setPage((p) => Math.min(pagination.pages, p + 1))
                }
                disabled={page >= pagination.pages}
                className="px-2.5 py-1 text-xs rounded-md border border-gray-200 disabled:opacity-40 hover:bg-gray-50"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </Card>

      <CreditDetailModal
        isOpen={!!detailId}
        creditId={detailId}
        onClose={() => setDetailId(null)}
      />
      <AddCreditModal
        isOpen={isAddOpen}
        onClose={() => setIsAddOpen(false)}
        onSuccess={handleCreditAdded}
      />
    </div>
  );
};

export default Credit;